import { useSearchPageMapData } from "@/providers/search";
import type { Filters } from "@/types/filters";
import { useEffect, useRef } from "react";

interface UseTotalCountFetchParams {
	filters: Partial<Filters>;
	enabled?: boolean;
}

/**
 * Hook to fetch total count of properties based on filters
 * Automatically refetches when filters change
 * Uses network-only fetch to ensure fresh data
 */
export function useTotalCountFetch({ filters, enabled = true }: UseTotalCountFetchParams) {
	const abortControllerRef = useRef<AbortController | null>(null);
	const fetchTotalCount = useSearchPageMapData(state => state.fetchTotalCount);

	useEffect(() => {
		if (!enabled) return;

		// Cancel any in-flight requests
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		// Create new abort controller for this request
		abortControllerRef.current = new AbortController();

		// Use the existing fetchTotalCount from the store
		fetchTotalCount({ filters });

		// Cleanup function to abort request on unmount or filter change
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
				abortControllerRef.current = null;
			}
		};
	}, [filters, enabled, fetchTotalCount]);
}
